import { useEffect, useRef, useState } from "react";
import {
  ILiveChatMessage,
  canRateConversation,
  isConversationWritable,
  parseOrderCard,
  parseQuickReplyOptions,
} from "../models/ILiveChat";
import { useLiveChat } from "../context/LiveChatContext";
import { useLanguage } from "../../../context/LanguageContext";
import { getUserToken } from "../../../utils/tokenUtils";
import "../../../styles/style.css";

/** Sunucudaki niyet eşleyici Türkçe çalışıyor; arayüz dili ne olursa olsun bu metni
 *  gönderiyoruz ki LiveChatBotService.WantsHumanAgent onu tanısın. */
const AGENT_REQUEST_PHRASE = "Temsilciye bağlan";

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp,image/heic";
const TYPING_DEBOUNCE_MS = 1200;

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

/**
 * Sunucu tam adı gönderiyor (LiveChatMapper.FullName). Müşteri tarafında soyad fazla —
 * temsilci panelinde tam ad kalıyor, temsilciler birbirini oradan ayırt ediyor.
 */
const firstNameOf = (fullName?: string | null): string =>
  fullName?.trim().split(/\s+/)[0] ?? "";

function StarPicker({ onPick, disabled }: { onPick: (v: number) => void; disabled?: boolean }) {
  const [hovered, setHovered] = useState(0);
  const [picked, setPicked] = useState(0);

  return (
    <div className="d-flex gap-1 justify-content-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`bi ${
            (hovered || picked) >= star ? "bi-star-fill text-warning" : "bi-star text-muted"
          }`}
          style={{ fontSize: "1.5rem", cursor: disabled ? "default" : "pointer" }}
          onMouseEnter={() => !disabled && setHovered(star)}
          onMouseLeave={() => !disabled && setHovered(0)}
          onClick={() => {
            if (disabled) return;
            setPicked(star);
            onPick(star);
          }}
        />
      ))}
    </div>
  );
}

function MessageBubble({
  message,
  currentUserId,
  onQuickReply,
  onRate,
  ratingDisabled,
}: {
  message: ILiveChatMessage;
  currentUserId: string | null;
  onQuickReply: (text: string) => void;
  onRate: (value: number) => void;
  ratingDisabled: boolean;
}) {
  const { t } = useLanguage();
  // Rol yerine kimliğe bakılır: sunucu gönderen rolünü JWT'den türettiği için admin
  // hesabıyla yazılan mesaj Agent olarak kaydedilir, ama balon yine bize aittir.
  const isMine = message.senderId
    ? message.senderId === currentUserId
    : message.senderRole === "Customer";
  const isPending = message.id.startsWith("pending-");

  if (message.messageType === "System") {
    return (
      <div className="livechat-system">
        <span>{message.content}</span>
      </div>
    );
  }

  if (message.messageType === "OrderCard") {
    const order = parseOrderCard(message.metadataJson);
    return (
      <div className="livechat-order-card">
        <div className="d-flex align-items-center gap-2 mb-1">
          <i className="bi bi-receipt text-danger"></i>
          <span className="fw-semibold small">
            {order?.orderNo ? `#${order.orderNo}` : t("liveChat.order")}
          </span>
          {order?.isCancelled && (
            <span className="badge bg-secondary rounded-pill">{t("liveChat.cancelled")}</span>
          )}
        </div>
        {order?.restaurantName && (
          <div className="small text-muted">
            <i className="bi bi-shop me-1"></i>
            {order.restaurantName}
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span className="small">{order?.statusName ?? message.content}</span>
          {order != null && (
            <span className="fw-bold small text-danger">{order.orderPrice.toFixed(2)}₺</span>
          )}
        </div>
      </div>
    );
  }

  if (message.messageType === "RatingRequest") {
    return (
      <div className="livechat-rating-card">
        <p className="small mb-2 text-center">{message.content}</p>
        <StarPicker onPick={onRate} disabled={ratingDisabled} />
      </div>
    );
  }

  const quickReplies =
    message.messageType === "QuickReply" ? parseQuickReplyOptions(message.metadataJson) : [];

  return (
    <div className={`livechat-row ${isMine ? "livechat-row--mine" : ""}`}>
      <div className={`livechat-bubble ${isMine ? "livechat-bubble--mine" : ""}`}>
        {!isMine && message.senderName && (
          <div className="livechat-bubble__sender">
            {message.senderRole === "Bot"
              ? t("liveChat.assistant")
              : firstNameOf(message.senderName)}
          </div>
        )}

        {message.messageType === "Image" && message.attachmentUrl && (
          <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer">
            {/* attachmentUrl mutlak S3 adresidir; S3_IMAGE_BASE_URL ile birleştirilmez. */}
            <img
              src={message.attachmentUrl}
              alt={message.attachmentName ?? ""}
              className="livechat-bubble__image"
            />
          </a>
        )}

        <div className="livechat-bubble__text">{message.content}</div>

        <div className="livechat-bubble__meta">
          <span>{formatTime(message.sentAt)}</span>
          {isMine &&
            (isPending ? (
              <i className="bi bi-clock" title={t("liveChat.sending")}></i>
            ) : (
              <i
                className={`bi ${message.isRead ? "bi-check2-all" : "bi-check2"}`}
                title={message.isRead ? t("liveChat.read") : t("liveChat.sent")}
              ></i>
            ))}
        </div>
      </div>

      {quickReplies.length > 0 && (
        <div className="livechat-chips">
          {quickReplies.map((option) => (
            <button key={option} className="livechat-chip" onClick={() => onQuickReply(option)}>
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveChatPanel() {
  const { t } = useLanguage();
  const {
    isOpen,
    openChat,
    closePanel,
    conversation,
    messages,
    queue,
    counterpartTyping,
    loading,
    sending,
    loadingMore,
    hasMore,
    error,
    clearError,
    sendMessage,
    sendAttachment,
    endConversation,
    submitRating,
    loadOlderMessages,
    notifyTyping,
  } = useLiveChat();

  const [draft, setDraft] = useState("");
  const [attachmentError, setAttachmentError] = useState("");
  const currentUserId = getUserToken()?.userId ?? null;
  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  const stickToBottomRef = useRef(true);

  // Kullanıcı yukarı kaydırdıysa yeni mesaj geldiğinde zorla aşağı atlamayalım.
  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 80;
  };

  useEffect(() => {
    if (!isOpen) return;
    const el = listRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, counterpartTyping, isOpen]);

  // Panel kapanırken "yazıyor" bilgisini temizle.
  useEffect(() => {
    if (isOpen) return;
    if (isTypingRef.current) {
      isTypingRef.current = false;
      notifyTyping(false);
    }
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
  }, [isOpen, notifyTyping]);

  const stopTyping = () => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    if (isTypingRef.current) {
      isTypingRef.current = false;
      notifyTyping(false);
    }
  };

  const handleDraftChange = (value: string) => {
    setDraft(value);
    if (!conversation) return;

    if (!isTypingRef.current && value.length > 0) {
      isTypingRef.current = true;
      notifyTyping(true);
    }

    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(stopTyping, TYPING_DEBOUNCE_MS);
  };

  const handleSend = async (text?: string) => {
    const content = (text ?? draft).trim();
    if (!content || sending) return;
    stopTyping();

    const fromDraft = text === undefined;
    if (fromDraft) setDraft("");

    const ok = await sendMessage(content);
    // Gönderilemediyse metni geri koy; kullanıcı yeniden yazmak zorunda kalmasın.
    if (!ok && fromDraft) setDraft(content);
  };

  const handleFilePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setAttachmentError("");
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachmentError(t("liveChat.attachmentTooLarge"));
      return;
    }
    await sendAttachment(file);
  };

  if (!isOpen) return null;

  const status = conversation?.status ?? "Bot";
  const writable = isConversationWritable(status);
  const showRating = canRateConversation(conversation);
  const botHandled = conversation?.isBotHandled ?? true;
  const headerName =
    conversation?.agentName && status === "Active"
      ? firstNameOf(conversation.agentName)
      : t("liveChat.assistant");

  const statusLabel: Record<string, string> = {
    Bot: t("liveChat.statusBot"),
    WaitingForAgent: t("liveChat.statusWaiting"),
    Active: t("liveChat.statusActive"),
    Resolved: t("liveChat.statusResolved"),
    Closed: t("liveChat.statusClosed"),
  };

  return (
    <div className="livechat-panel" role="dialog" aria-label={t("liveChat.title")}>
      <div className="livechat-panel__header background-red">
        <div className="d-flex align-items-center gap-2 flex-grow-1 text-white">
          <i className={`bi ${status === "Active" ? "bi-headset" : "bi-robot"} fs-5`}></i>
          <div className="lh-sm">
            <div className="fw-semibold small">{headerName}</div>
            <div className="livechat-panel__status">{statusLabel[status]}</div>
          </div>
        </div>

        {/* Sohbeti bitirme başlıktan kaldırıldı: paneli kapatan X'in yanında etiketsiz
            bir ikon olarak ikisi karışıyordu. Artık yazma alanının üstünde, adı yazan
            bir düğme. */}
        <button
          className="btn-close btn-close-white ms-1"
          onClick={closePanel}
          aria-label={t("common.close")}
        />
      </div>

      {conversation?.conversationNo && (
        <div className="livechat-panel__subheader">
          <i className="bi bi-hash"></i>
          {conversation.conversationNo}
        </div>
      )}

      {queue && status === "WaitingForAgent" && (
        <div className="livechat-queue">
          <i className="bi bi-people-fill me-2"></i>
          {queue.agentsAvailable ? (
            <span>
              {t("liveChat.queuePosition", {
                position: queue.position,
                minutes: queue.estimatedWaitMinutes,
              })}
            </span>
          ) : (
            <span>{t("liveChat.noAgentsOnline")}</span>
          )}
        </div>
      )}

      <div className="livechat-panel__body" ref={listRef} onScroll={handleScroll}>
        {loading && (
          <div className="text-center py-4">
            <span className="spinner-border spinner-border-sm text-danger" />
          </div>
        )}

        {!loading && hasMore && (
          <div className="text-center mb-2">
            <button
              className="btn btn-sm btn-outline-secondary rounded-pill"
              onClick={loadOlderMessages}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                t("liveChat.loadOlder")
              )}
            </button>
          </div>
        )}

        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            currentUserId={currentUserId}
            onQuickReply={(text) => handleSend(text)}
            onRate={(value) => submitRating(value)}
            ratingDisabled={conversation?.rating != null}
          />
        ))}

        {counterpartTyping && (
          <div className="livechat-row">
            <div className="livechat-bubble livechat-typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        {showRating && (
          <div className="livechat-rating-card">
            <p className="small mb-2 text-center fw-semibold">{t("liveChat.rateTitle")}</p>
            <StarPicker onPick={(value) => submitRating(value)} />
          </div>
        )}

        {conversation?.rating != null && (
          <div className="livechat-system">
            <span>{t("liveChat.rateThanks")}</span>
          </div>
        )}
      </div>

      {(error || attachmentError) && (
        <div className="alert alert-danger py-2 px-3 small mb-0 rounded-0 d-flex align-items-center">
          <i className="bi bi-exclamation-circle me-2"></i>
          <span className="flex-grow-1">{error || attachmentError}</span>
          <button
            className="btn btn-sm btn-link text-danger p-0"
            onClick={() => {
              clearError();
              setAttachmentError("");
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      {writable && (
        <div className="livechat-actions">
          {/* Bunu basmak sohbeti kapatır ve IsBotHandled true kaldığı için asistanın
              tek başına çözdüğü sohbet olarak sayılır (Bot Çözme metriği). */}
          <button className="livechat-action livechat-action--resolve" onClick={endConversation}>
            <i className="bi bi-check2-circle me-1"></i>
            {botHandled ? t("liveChat.resolved") : t("liveChat.endChat")}
          </button>

          {botHandled && status !== "WaitingForAgent" && (
            /* Doğrudan /request-agent çağırmıyoruz: o uç nokta botu atlayıp sohbeti
               kuyruğa atıyor, teslimat süresi kuralı devreye girmiyordu. Çip ile aynı
               yoldan geçirince asistan ya süreyi hatırlatıyor ya da devrediyor. */
            <button
              className="livechat-action livechat-action--agent"
              onClick={() => handleSend(AGENT_REQUEST_PHRASE)}
              disabled={sending}
            >
              <i className="bi bi-person-raised-hand me-1"></i>
              {t("liveChat.requestAgent")}
            </button>
          )}
        </div>
      )}

      {writable ? (
        <div className="livechat-composer">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            className="d-none"
            onChange={handleFilePick}
          />
          <button
            className="livechat-composer__icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            title={t("liveChat.attachPhoto")}
          >
            <i className="bi bi-paperclip"></i>
          </button>

          <textarea
            className="livechat-composer__input"
            rows={1}
            maxLength={2000}
            value={draft}
            placeholder={t("liveChat.placeholder")}
            onChange={(e) => handleDraftChange(e.target.value)}
            onBlur={stopTyping}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />

          <button
            className="livechat-composer__send"
            onClick={() => handleSend()}
            disabled={sending || draft.trim().length === 0}
            aria-label={t("liveChat.send")}
          >
            {sending ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="bi bi-send-fill"></i>
            )}
          </button>
        </div>
      ) : (
        <div className="livechat-closed-note">
          <div className="mb-2">
            <i className="bi bi-lock me-2"></i>
            {t("liveChat.closedNote")}
          </div>
          {/* Kapanan sohbet yeniden kullanılamıyor; buradan yeni bir sohbet açılır
              ki kullanıcı paneli kapatıp destek düğmesini aramak zorunda kalmasın. */}
          <button
            className="btn btn-sm btn-danger rounded-pill px-3"
            onClick={() => openChat()}
            disabled={loading}
          >
            <i className="bi bi-chat-dots me-1"></i>
            {t("liveChat.newChat")}
          </button>
        </div>
      )}
    </div>
  );
}

export default LiveChatPanel;
